// TennisTrack Hub — Scheda Medico | 029 Project
// Profilo completo: curriculum, convenzioni, contatto rapido, prenotazione
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import api from '../../utils/api';
import Navbar from '../../components/Navbar';
import './Medici.css';

const SchedaMedico = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [medico, setMedico] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errore, setErrore] = useState('');

  // Form contatto
  const [mostraForm, setMostraForm] = useState(false);
  const [formMsg, setFormMsg] = useState({ oggetto: '', testo: '' });
  const [msgLoading, setMsgLoading] = useState(false);
  const [msgEsito, setMsgEsito] = useState('');
  const [msgErrore, setMsgErrore] = useState('');

  useEffect(() => {
    api.get(`/medici/${id}`)
      .then(res => setMedico(res.data.medico))
      .catch(err => {
        if (err.response?.status === 404) {
          setErrore('Specialista non trovato.');
        } else {
          setErrore('Errore nel caricamento della scheda.');
        }
      })
      .finally(() => setLoading(false));
  }, [id]);

  const inviaMessaggio = async (e) => {
    e.preventDefault();
    if (!formMsg.testo.trim()) return;
    setMsgLoading(true);
    setMsgEsito('');
    setMsgErrore('');
    try {
      await api.post(`/medici/${id}/contatta`, formMsg);
      setMsgEsito('✅ Messaggio inviato con successo! Lo specialista ti risponderà al più presto.');
      setFormMsg({ oggetto: '', testo: '' });
      setMostraForm(false);
    } catch (err) {
      setMsgErrore(err.response?.data?.error || 'Errore nell\'invio del messaggio.');
    } finally {
      setMsgLoading(false);
    }
  };

  if (loading) return (
    <div className="page-layout">
      <Navbar />
      <main className="page-main">
        <div className="loading-msg">Caricamento scheda medico...</div>
      </main>
    </div>
  );

  if (errore || !medico) return (
    <div className="page-layout">
      <Navbar />
      <main className="page-main" style={{ textAlign: 'center', padding: '60px 24px' }}>
        <div style={{ fontSize: '3rem', marginBottom: '16px' }}>🩺</div>
        <h2>{errore || 'Scheda non disponibile'}</h2>
        <button className="btn btn-secondary" style={{ marginTop: '20px' }} onClick={() => navigate('/medici')}>
          ← Torna alla ricerca
        </button>
      </main>
    </div>
  );

  const m = medico;

  return (
    <div className="page-layout">
      <Navbar />
      <main className="page-main">
        <div className="scheda-container">

          {/* Breadcrumb */}
          <div className="scheda-breadcrumb">
            <Link to="/medici" className="breadcrumb-link">🩺 Medical Network</Link>
            <span className="breadcrumb-sep">›</span>
            <span>{m.nome} {m.cognome}</span>
          </div>

          {/* Header scheda */}
          <div className="scheda-header card">
            <div className="scheda-header-main">
              {m.foto_url ? (
                <img src={m.foto_url} alt={`${m.nome} ${m.cognome}`} className="scheda-foto" />
              ) : (
                <div className="scheda-foto-placeholder">
                  {m.nome?.[0]}{m.cognome?.[0]}
                </div>
              )}
              <div className="scheda-info">
                <div className="scheda-spec-badge">
                  {m.spec_icona} {m.spec_nome}
                </div>
                <h1 className="scheda-nome">
                  {m.titolo ? `${m.titolo} ` : ''}{m.nome} {m.cognome}
                </h1>
                {m.studio_nome && (
                  <div className="scheda-studio">🏢 {m.studio_nome}</div>
                )}
                <div className="scheda-localita">
                  📍 {m.indirizzo && `${m.indirizzo}, `}{m.citta}{m.cap ? ` ${m.cap}` : ''}{m.provincia ? ` (${m.provincia})` : ''}
                </div>
                {m.anni_esperienza && (
                  <div className="scheda-exp">⏱ {m.anni_esperienza} anni di esperienza</div>
                )}
                {m.lingue && m.lingue.length > 0 && (
                  <div className="scheda-lingue">🌐 {m.lingue.join(', ')}</div>
                )}
              </div>
            </div>

            {/* CTA Azioni */}
            <div className="scheda-actions">
              {m.link_prenotazione && (
                <a
                  href={m.link_prenotazione}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-primary btn-lg"
                >
                  📅 Prenota una visita
                </a>
              )}
              {m.accetta_messaggi && (
                <button
                  className="btn btn-medical btn-lg"
                  onClick={() => setMostraForm(!mostraForm)}
                >
                  💬 Contatta lo specialista
                </button>
              )}
              {m.telefono && (
                <a href={`tel:${m.telefono}`} className="btn btn-secondary btn-lg">
                  📞 {m.telefono}
                </a>
              )}
            </div>
          </div>

          {/* Esito messaggio */}
          {msgEsito && (
            <div className="alert alert-success">{msgEsito}</div>
          )}

          {/* Form contatto in-app */}
          {mostraForm && (
            <div className="scheda-form-contatto card">
              <h3>💬 Invia un messaggio a {m.nome} {m.cognome}</h3>
              {msgErrore && <div className="alert alert-error" style={{ marginBottom: '12px' }}>{msgErrore}</div>}
              <form onSubmit={inviaMessaggio}>
                <div className="form-group">
                  <label className="form-label">Oggetto (opzionale)</label>
                  <input
                    type="text"
                    className="form-input"
                    placeholder="Es. Consulenza infortunio spalla"
                    value={formMsg.oggetto}
                    onChange={e => setFormMsg(f => ({ ...f, oggetto: e.target.value }))}
                    maxLength={200}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Messaggio *</label>
                  <textarea
                    className="form-textarea"
                    rows={4}
                    placeholder="Descrivi il tuo problema o la richiesta..."
                    value={formMsg.testo}
                    onChange={e => setFormMsg(f => ({ ...f, testo: e.target.value }))}
                    maxLength={1000}
                    required
                  />
                </div>
                <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setMostraForm(false)}
                  >
                    Annulla
                  </button>
                  <button
                    type="submit"
                    className="btn btn-medical"
                    disabled={msgLoading || !formMsg.testo.trim()}
                  >
                    {msgLoading ? '⏳ Invio...' : '📨 Invia messaggio'}
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Contenuto principale — 2 colonne */}
          <div className="scheda-body">
            {/* Colonna sinistra */}
            <div className="scheda-col-main">

              {/* Curriculum / Bio */}
              {m.curriculum && (
                <div className="card scheda-section">
                  <h2 className="scheda-section-title">📋 Curriculum e Profilo</h2>
                  <div className="scheda-curriculum">
                    {m.curriculum.split('\n').map((par, i) => (
                      <p key={i}>{par}</p>
                    ))}
                  </div>
                </div>
              )}

              {/* Circoli convenzionati */}
              {m.circoli_convenzionati && m.circoli_convenzionati.length > 0 && (
                <div className="card scheda-section">
                  <h2 className="scheda-section-title">🎾 Circoli Convenzionati</h2>
                  <div className="circoli-list">
                    {m.circoli_convenzionati.map((c, i) => (
                      <span key={i} className="circolo-badge">{c}</span>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Colonna destra — info pratiche */}
            <div className="scheda-col-side">

              {/* Convenzioni e tariffe */}
              <div className="card scheda-section">
                <h2 className="scheda-section-title">💳 Convenzioni & Tariffe</h2>
                {m.convenzioni ? (
                  <div className="scheda-info-row">
                    <span className="scheda-info-label">Convenzioni</span>
                    <span className="scheda-info-value">{m.convenzioni}</span>
                  </div>
                ) : (
                  <p style={{ color: 'var(--text-secondary)', fontSize: '0.88rem' }}>Nessuna convenzione indicata.</p>
                )}
                {m.tariffa_visita && (
                  <div className="scheda-info-row">
                    <span className="scheda-info-label">Visita indicativa</span>
                    <span className="scheda-info-value scheda-tariffa">€{parseFloat(m.tariffa_visita).toFixed(2)}</span>
                  </div>
                )}
              </div>

              {/* Orari e contatti */}
              <div className="card scheda-section">
                <h2 className="scheda-section-title">🕐 Orari & Contatti</h2>
                {m.orari_ricevimento && (
                  <div className="scheda-info-row">
                    <span className="scheda-info-label">Orari</span>
                    <span className="scheda-info-value" style={{ whiteSpace: 'pre-line' }}>
                      {m.orari_ricevimento}
                    </span>
                  </div>
                )}
                {m.email_contatto && (
                  <div className="scheda-info-row">
                    <span className="scheda-info-label">Email</span>
                    <a href={`mailto:${m.email_contatto}`} className="scheda-link">{m.email_contatto}</a>
                  </div>
                )}
                {m.telefono && (
                  <div className="scheda-info-row">
                    <span className="scheda-info-label">Telefono</span>
                    <a href={`tel:${m.telefono}`} className="scheda-link">{m.telefono}</a>
                  </div>
                )}
              </div>

              {/* Prenotazione rapida */}
              {m.link_prenotazione && (
                <div className="card scheda-section scheda-prenota-box">
                  <h2 className="scheda-section-title">📅 Prenota Online</h2>
                  <p style={{ fontSize: '0.88rem', color: 'var(--text-secondary)', marginBottom: '14px' }}>
                    Prenota direttamente la tua visita su piattaforma esterna.
                  </p>
                  <a
                    href={m.link_prenotazione}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn btn-primary"
                    style={{ width: '100%', textAlign: 'center', justifyContent: 'center' }}
                  >
                    📅 Prenota una visita
                  </a>
                </div>
              )}
            </div>
          </div>

        </div>
      </main>
    </div>
  );
};

export default SchedaMedico;
